import { optionsColumn } from '@/global/components/DataGrid/hooks/useDataGridPro'
import {
    ColSpanGridParams,
    GridRenderCellParams,
} from '@/global/components/DataGrid/utils/types'
import HorizontalDivider from '@/global/components/Divider/HorizontalDivider'
import { FormField } from '@/global/components/Form/TextField'
import { FormFieldTime } from '@/global/components/Form/Time'
import Menu from '@/global/components/Menu'
import Grid from '@/global/components/ui/Grid'
import ContentGuide from '@/global/guides/ContentGuide'
import { useStore } from '@/store'
import {
    calcularJornada,
    hoursToMinutes,
    minutesToHours,
    normalize,
} from '@acme/common'
import { BrushCleaningIcon, RepeatIcon } from 'lucide-react'
import React, { useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { match } from 'ts-pattern'
import { diasTabelaHorario } from '../constants/dias-tabela-horario'
import { useCanSubmit } from '../context/CanSubmitContext'
import { useNoturnoPreview } from '../hooks/use-noturno-preview'
import { NoturnoPreviewHeader } from './NoturnoPreviewHeader'

const diasSemana = Array.from({ length: 7 }).map((_, index) => ({
    dia: diasTabelaHorario[index],
    index,
}))

export function HorariosForm() {
    const empresa = useStore(state => state.company)
    const { getValues, setValue, watch } = useFormContext()

    const isEscala = watch('tipo') === 'ESCALA'
    const lastUpdate = watch('updatedAt')

    const { noturno } = useNoturnoPreview()

    useEffect(() => {
        if (!empresa) return

        const calculateAllDays = async () => {
            const indices = isEscala
                ? [0]
                : Array.from({ length: 7 }, (_, i) => i)

            if (!noturno?.utilizaReducao) {
                indices.forEach(index => {
                    setValue(`horarios[${index}].totReduzido`, '')
                })
                return setValue('totReduzido', '')
            }

            // Calcula os dias
            const dayCalculations = await Promise.all(
                indices.map(async index => {
                    const horarios = getValues(
                        Array.from({ length: 3 }).flatMap((_, i) =>
                            ['Ent', 'Sai'].map(
                                prefix =>
                                    `horarios[${index}].${prefix}${i + 1}`,
                            ),
                        ),
                    ).filter(Boolean)
                    const totalReduzido = await Promise.resolve(
                        calcularJornada(
                            {
                                horarioFimAdicionalNoturno:
                                    noturno?.horarioFimAdicionalNoturno,
                                horarioInicioAdicionalNoturno:
                                    noturno?.horarioInicioAdicionalNoturno,
                                utilizaReducao:
                                    noturno?.utilizaReducao ?? false,
                            },
                            ...horarios,
                        ),
                    )
                    setValue(`horarios[${index}].totReduzido`, totalReduzido)
                    return { index, totalReduzido }
                }),
            )

            if (!isEscala) {
                // Calcula o total semanal após todos os dias serem processados
                const totaisReducao = dayCalculations.map(
                    ({ totalReduzido }) => totalReduzido,
                )
                const reducaoSemanal = totaisReducao.reduce(
                    (acc, curr) => acc + hoursToMinutes(curr),
                    0,
                )

                setValue(`totReduzido`, minutesToHours(reducaoSemanal))
            }
        }

        calculateAllDays()
    }, [empresa?.id, lastUpdate, noturno, isEscala])

    return (
        <Grid container>
            <Grid item xs={12} className="mb-4 mt-8">
                <HorizontalDivider>Horários</HorizontalDivider>
            </Grid>
            <Grid item xs={12}>
                <ContentGuide.DataGrid
                    loading={false}
                    columns={[
                        {
                            ...optionsColumn,
                            field: 'dia',
                            headerName: 'Dia',
                            flex: 1,
                            renderCell: ({ value, row }) =>
                                row.index === 7 ? null : (
                                    <span className="select-none">{value}</span>
                                ),
                            disableColumnMenu: true,
                        },
                        ...Array.from({ length: 3 }).flatMap((_, i) =>
                            ['Entrada', 'Saída'].map(es => ({
                                ...optionsColumn,
                                field: `${normalize(es).slice(0, 3)}${i + 1}`,
                                headerName: es,
                                headerAlign: 'center' as const,
                                align: 'center' as const,
                                width: 90,
                                colSpan: (
                                    params: ColSpanGridParams<any, any>,
                                ) => {
                                    if (
                                        params.row.index === 7 &&
                                        es === 'Entrada' &&
                                        i === 2
                                    )
                                        return 2
                                    return 1
                                },
                                renderCell: (params: GridRenderCellParams) => {
                                    if (params.row.index === 7) {
                                        if (params.field === 'Ent3')
                                            return (
                                                <span
                                                    className="select-none w-full text-right"
                                                    title="Total semanal"
                                                >
                                                    Total semanal
                                                </span>
                                            )
                                        return ''
                                    }

                                    return <CellEntradaSaida {...params} />
                                },
                                disableColumnMenu: true,
                            })),
                        ),
                        {
                            ...optionsColumn,
                            align: 'center',
                            headerAlign: 'center',
                            field: 'totSem',
                            headerName: 'Total',
                            width: 95,
                            renderCell: ({ row }) => (
                                <CellTotal index={row.index} />
                            ),
                            disableColumnMenu: true,
                        },
                        {
                            ...optionsColumn,
                            align: 'center',
                            headerAlign: 'center',
                            field: 'totReduzido',
                            headerName: 'Reduzido',
                            width: 95,
                            renderCell: ({ row }) => (
                                <CellTotalReduzido index={row.index} />
                            ),
                            disableColumnMenu: true,
                        },
                        {
                            ...optionsColumn,
                            field: '__actions',
                            type: 'actions',
                            width: 45,
                            headerAlign: 'center',
                            align: 'center',
                            renderHeader: () => <NoturnoPreviewHeader />,
                            getActions: ({ row }) => {
                                if (row.index === 7) return []
                                if (isEscala) {
                                    return [
                                        <LimparHorariosItem
                                            key={`${row.index}-limpar`}
                                            index={row.index}
                                        />,
                                    ]
                                }
                                return [
                                    <RepetirHorariosItem
                                        key={`${row.index}-repetir`}
                                        index={row.index}
                                    />,
                                    <LimparHorariosItem
                                        key={`${row.index}-limpar`}
                                        index={row.index}
                                    />,
                                ]
                            },
                        },
                    ]}
                    rows={
                        isEscala
                            ? [{ dia: 'Horário', index: 0, id: 0 }]
                            : [
                                  ...diasSemana,
                                  {
                                      dia: '',
                                      index: 7,
                                  },
                              ].map(it => ({ ...it, id: it.index }))
                    }
                    ToolbarProps={{ hideToolbar: true }}
                    slots={{ Footer: () => null }}
                />
            </Grid>
        </Grid>
    )
}

const CellEntradaSaida = React.memo(function CellEntradaSaida({
    row: { index },
    field,
}: GridRenderCellParams<any, { dia: string; index: number }>) {
    const { setValue, getValues } = useFormContext()
    const { setCanSubmit } = useCanSubmit()

    function onBlur(field: any) {
        return function handleBlur(e: React.FocusEvent<HTMLInputElement>) {
            const value = e.currentTarget.value || ''
            const hora = match(value.length)
                .with(0, () => '')
                .with(1, () => `0${value}:00`)
                .otherwise(() => value.padEnd(5, '0'))
            setValue(field, hora)

            const horariosDia: string[] = getValues(
                Array.from({ length: 3 }).flatMap((_, i) =>
                    ['Ent', 'Sai'].map(
                        prefix => `horarios[${index}].${prefix}${i + 1}`,
                    ),
                ),
            ).filter(Boolean)
            if (horariosDia.length % 2 !== 0) horariosDia.pop()

            const totalDia = calcularJornada(false, ...horariosDia)
            setValue(`horarios[${index}].Tot`, totalDia)
            setValue('updatedAt', new Date().getTime())

            setCanSubmit(true)
        }
    }

    const onFocus = () => setCanSubmit(false)

    return (
        <FormFieldTime
            variant="outlined"
            name={`horarios[${index}].${field}`}
            size="small"
            unHelperText
            onBlur={onBlur(`horarios[${index}].${field}`)}
            onFocus={onFocus}
        />
    )
})

const RepetirHorariosItem = React.memo(function RepetirHorariosItem({
    index,
}: {
    index: number
}) {
    const { getValues, setValue } = useFormContext()
    return (
        <Menu.Item
            key="repetir"
            onClick={() => {
                const horario = getValues(`horarios[${index}]`)

                Array.from({ length: 7 }).forEach((_, i) => {
                    if (i !== index) {
                        setValue(`horarios[${i}]`, {
                            ...horario,
                            label: diasSemana[i].dia,
                        })
                    }
                })
                setValue('updatedAt', new Date().getTime())
            }}
        >
            <Menu.Item.Icon>
                <RepeatIcon />
            </Menu.Item.Icon>
            <Menu.Item.Text>Repetir horários</Menu.Item.Text>
        </Menu.Item>
    )
})

const LimparHorariosItem = React.memo(function LimparHorariosItem({
    index,
}: {
    index: number
}) {
    const { setValue } = useFormContext()

    return (
        <Menu.Item
            key="limpar"
            onClick={() => {
                setValue(`horarios[${index}]`, {
                    label: diasSemana[index]?.dia ?? 'Horário',
                    Ent1: '',
                    Sai1: '',
                    Ent2: '',
                    Sai2: '',
                    Ent3: '',
                    Sai3: '',
                    Tot: '00:00',
                    totReduzido: '00:00',
                })
                setValue('updatedAt', new Date().getTime())
            }}
        >
            <Menu.Item.Icon>
                <BrushCleaningIcon />
            </Menu.Item.Icon>
            <Menu.Item.Text>Limpar horários</Menu.Item.Text>
        </Menu.Item>
    )
})

const CellTotal = React.memo(function CellTotal({ index }: { index: number }) {
    const { watch, setValue } = useFormContext()

    const totais = watch(
        Array.from({ length: 7 }).map((_, i) => `horarios[${i}].Tot`),
    )

    useEffect(() => {
        if (index !== 7) return
        const totalSemanal = totais.reduce((acc, curr) => {
            if (curr) acc += hoursToMinutes(curr)
            return acc
        }, 0)

        setValue(`totSem`, minutesToHours(totalSemanal))
    }, totais)

    if (index === 7)
        return (
            <FormField
                variant="outlined"
                name="totSem"
                disabled
                size="small"
                unHelperText
            />
        )
    return (
        <FormFieldTime
            variant="outlined"
            name={`horarios[${index}].Tot`}
            disabled
            size="small"
            unHelperText
        />
    )
})

const CellTotalReduzido = React.memo(function CellTotalReduzido({
    index,
}: {
    index: number
}) {
    if (index === 7)
        return (
            <FormField
                variant="outlined"
                name="totReduzido"
                disabled
                size="small"
                unHelperText
            />
        )
    return (
        <FormFieldTime
            variant="outlined"
            name={`horarios[${index}].totReduzido`}
            disabled
            size="small"
            unHelperText
        />
    )
})
